import { useEffect, useRef, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { formatCacheAge } from '../../lib/offlineCache';

type BannerState = 'hidden' | 'offline' | 'back-online' | 'leaving';

export function OfflineBanner({ cachedAt }: { cachedAt?: string | null }) {
  const [state, setState] = useState<BannerState>(
    navigator.onLine ? 'hidden' : 'offline',
  );
  const wasOfflineRef = useRef(!navigator.onLine);

  useEffect(() => {
    function handleOffline() {
      wasOfflineRef.current = true;
      setState('offline');
    }

    function handleOnline() {
      if (!wasOfflineRef.current) return;
      wasOfflineRef.current = false;
      setState('back-online');
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Auto-dismiss the "Back online" message after a short delay
  useEffect(() => {
    if (state !== 'back-online') return;

    const timer = setTimeout(() => {
      setState('leaving');
    }, 2000);

    return () => clearTimeout(timer);
  }, [state]);

  // After the leave animation completes, hide entirely
  useEffect(() => {
    if (state !== 'leaving') return;

    const timer = setTimeout(() => {
      setState('hidden');
    }, 300);

    return () => clearTimeout(timer);
  }, [state]);

  if (state === 'hidden') return null;

  const isOnline = state === 'back-online' || state === 'leaving';
  const isLeaving = state === 'leaving';

  return (
    <div
      role="status"
      aria-live="polite"
      className={`shrink-0 overflow-hidden transition-all duration-300 ease-out ${
        isLeaving
          ? 'max-h-0 opacity-0'
          : 'max-h-12 opacity-100'
      }`}
    >
      <div
        className={`flex items-center justify-center gap-2 px-4 py-1.5 border-b text-xs font-medium ${
          isOnline
            ? 'bg-sage/15 border-sage/20 text-sage'
            : 'bg-amber-500/15 border-amber-500/20 text-amber-400'
        }`}
      >
        {isOnline ? <Wifi size={14} /> : <WifiOff size={14} />}
        <span className="text-sm">
          {isOnline
            ? 'Back online'
            : cachedAt
              ? `Offline — last updated ${formatCacheAge(cachedAt)}`
              : "You're offline"}
        </span>
      </div>
    </div>
  );
}
